import React, { useEffect, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Btn, Fld, Inp } from "@/components/portal/crm/kit";
import { Modal } from "@/components/portal/sys/Modal";
import "@/styles/deal-pitch.css";

const OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Addresses as chips — type, press Enter or comma, pick a saved one. */
function ToField({ value, onChange, saved, testId }) {
  const [draft, setDraft] = useState("");
  const add = (raw, quiet = false) => {
    const e = raw.trim().replace(/[,;]$/, "").toLowerCase();
    if (!e) return;
    if (!OK.test(e)) { if (!quiet) toast.error(`${e} doesn't look like an email address`); return; }
    if (value.length >= 5) { toast.error("Up to five addresses at a time"); return; }
    if (!value.includes(e)) onChange([...value, e]);
    setDraft("");
  };
  const spare = saved.filter((e) => !value.includes(e));
  return (
    <>
      <div className="dp-to" data-testid={testId}>
        {value.map((e) => (
          <span key={e} className="dp-chip">{e}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} aria-label={`Remove ${e}`}
                    data-testid={`${testId}-remove-${e}`}><X className="size-3" /></button>
          </span>
        ))}
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={value.length ? "Add another" : "credit@yourfirm.co.uk"}
               onKeyDown={(e) => { if (["Enter", ",", ";"].includes(e.key)) { e.preventDefault(); add(draft); } }}
               onBlur={() => add(draft, true)} inputMode="email" data-testid={`${testId}-input`} />
      </div>
      {spare.length > 0 && (
        <div className="dp-saved" data-testid={`${testId}-saved`}>
          <span>Used before:</span>
          {spare.map((e) => <button key={e} type="button" onClick={() => add(e)} data-testid={`${testId}-saved-${e}`}>{e}</button>)}
        </div>
      )}
    </>
  );
}

/**
 * Email the company check or the deal pack from the desk, as one PDF stamped with the desk's name.
 * `doc` picks what goes; a broker desk is talking to its lender panel, a direct lender to its credit team.
 */
export function SendDialog({ leadId, doc, company, onClose, testId = "send-dialog" }) {
  const [prefs, setPrefs] = useState(null);
  const [to, setTo] = useState([]);
  const [note, setNote] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/lender/send-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({ emails: [] })); }, []);
  const broker = !!prefs?.broker;
  const what = doc === "deal_pack" ? "deal pack" : "company check";

  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/lender/leads/${leadId}/send`, { doc, to, note: note.trim() || null, remember });
      toast.success(`${what[0].toUpperCase()}${what.slice(1)} sent to ${r.data.sent.join(", ")}${r.data.via === "link" ? " as a secure link" : ""}`);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send — try again in a moment"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => onClose(false)} width={560} testId={testId}
           title={`Email the ${what}${broker ? " to your lenders" : " to credit"}`}
           sub={`${company || "This case"} — one PDF with ${prefs?.desk || "your desk"}'s name on every page. Replies come straight back to you.`}
           footer={
             <div className="dp-foot">
               <Btn onClick={() => onClose(false)} testId={`${testId}-cancel`}>Not now</Btn>
               <Btn kind="primary" onClick={go} disabled={busy || !to.length} testId={`${testId}-send`}>
                 {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                 {busy ? "Making the PDF and sending…" : !to.length ? "Send" : `Send to ${to.length} ${to.length === 1 ? "address" : "addresses"}`}
               </Btn>
             </div>
           }>
      <Fld label={broker ? "Your lenders — up to five" : "Your credit team — up to five"}>
        <ToField value={to} onChange={setTo} saved={prefs?.emails || []} testId={`${testId}-to`} />
      </Fld>
      <Fld label="A note with it (optional)">
        <textarea className="inp" rows={3} maxLength={600} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={broker ? "e.g. Strong trading history — keen to hear your appetite" : "e.g. For Thursday's credit meeting"}
                  data-testid={`${testId}-note`} />
      </Fld>
      <label className="dp-remember">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid={`${testId}-remember`} />
        Remember these addresses for next time
      </label>
      <p className="dp-fine">
        The email comes from <b>{prefs?.desk || "your desk"} via Connexis</b>. Very large packs go as a secure link that
        works for 14 days instead of an attachment.
      </p>
    </Modal>
  );
}
